import { createContext, useContext } from 'react'

/**
 * SHL-33 (David, 10 Oct: "One Admin entry -> admin home").
 *
 * The identity menu used to list every Support and Administration page the
 * person may open — eight rows for a comms admin who also moderates, which is
 * how the menu ran off the bottom of a short window (SUP-1). It now carries
 * ONE row, "Admin", which opens #/admin: a page of cards, one per destination.
 *
 * ── THE CARDS ARE THE RAIL'S LIST, NOT A SECOND GATE (rule 11) ──────────────
 *
 * AppShell filters NAV once, by the same flags it always has, and provides the
 * Support and Administration groups through this context. The admin home
 * draws whatever arrives. Two copies of "who may see Moderation" would be two
 * answers, and they would disagree exactly where it matters.
 *
 * Its own module rather than AppShell.tsx for openLanes.ts's reason: the shell
 * imports an SVG the test bundler cannot resolve, so a component that imports
 * the shell is one the suite cannot mount. Read it from a CHILD of the shell
 * (`AdminHomeCards`), never from the route body, which sits above the provider.
 */
export type AdminDestination = {
  /** NAV key (English); the display goes through navLabel. */
  label: string
  /** Route without the leading slash, as NAV writes it. */
  route: string
  badge?: number
}

export type AdminGroup = {
  /** NAV heading key: 'Support' | 'Administration'. */
  heading: string
  items: AdminDestination[]
}

export const AdminDestinationsContext = createContext<AdminGroup[]>([])

export function useAdminDestinations(): AdminGroup[] {
  return useContext(AdminDestinationsContext)
}

/** The route of the admin home itself — never one of its own cards. */
export const ADMIN_HOME_ROUTE = 'admin'

/** Everything the admin home would show, flattened — empty = no Admin row. */
export function adminDestinationCount(groups: AdminGroup[]): number {
  return groups.reduce((n, g) => n + g.items.length, 0)
}

/** What waits across the destinations (Moderation's queue today). */
export function adminBadge(groups: AdminGroup[]): number {
  return groups.reduce((n, g) => n + g.items.reduce((m, i) => m + (i.badge ?? 0), 0), 0)
}
